import type { AnchorHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { trackGtmButtonClick } from '@/analytics/gtm'

export type TrackedLinkProps = Omit<LinkProps, 'onClick'> & {
  gtmId?: string
  gtmLabel?: string
  gtmSection?: string
  gtmExtra?: Record<string, unknown>
  onClick?: LinkProps['onClick']
}

/**
 * Same as `Link`, but fires `button_click` when `gtmId` is set.
 * Optional `gtmExtra` merges into the dataLayer payload (e.g. content_id for cards).
 */
export function TrackedLink({
  to,
  gtmId,
  gtmLabel,
  gtmSection,
  gtmExtra,
  onClick,
  children,
  ...rest
}: TrackedLinkProps) {
  const destination = typeof to === 'string' ? to : undefined
  return (
    <Link
      to={to}
      onClick={(e) => {
        if (gtmId) {
          const resolvedLabel = gtmLabel ?? (typeof children === 'string' ? children : undefined)
          trackGtmButtonClick({
            button_id: gtmId,
            ...(resolvedLabel != null && { label: resolvedLabel }),
            ...(gtmSection != null && { section: gtmSection }),
            ...(destination != null && { destination }),
            ...gtmExtra,
          })
        }
        onClick?.(e)
      }}
      {...rest}
    >
      {children}
    </Link>
  )
}

export type TrackedAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  gtmId?: string
  gtmLabel?: string
  gtmSection?: string
  gtmExtra?: Record<string, unknown>
}

/**
 * Same as a plain `<a>`, but fires `button_click` when `gtmId` is set.
 */
export function TrackedAnchor({
  href,
  gtmId,
  gtmLabel,
  gtmSection,
  gtmExtra,
  onClick,
  children,
  ...rest
}: TrackedAnchorProps) {
  return (
    <a
      href={href}
      onClick={(e) => {
        if (gtmId) {
          const resolvedLabel = gtmLabel ?? (typeof children === 'string' ? children : undefined)
          trackGtmButtonClick({
            button_id: gtmId,
            ...(resolvedLabel != null && { label: resolvedLabel }),
            ...(gtmSection != null && { section: gtmSection }),
            ...(href != null && { destination: href }),
            ...gtmExtra,
          })
        }
        onClick?.(e)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
